import { FC, useEffect, useRef, useState } from 'react';

import { CellProps, Column } from 'react-table';

import {
  DownloadIcon,
  LogoutIcon,
  CollapseIcon,
  ExpandIcon,
  SwitchReloadIcon
} from 'assets/icons';
import threeShapeImage from 'assets/images/3shape.png';
import { ChipLabel, Table } from 'components';
import { If } from 'components/Generics';
import TableSkeleton from 'components/TableSkeleton/TableSkeleton';
import { DATE_FORMAT_LONG_MONTH } from 'constants/settings';
import { RESUBMISSION_STATUS } from 'constants/treatmentFiles';
import { useTreatmentContext } from 'contexts/treatment.context';
import {
  ThreeShapeCase,
  ThreeShapeDocumentUploadUrl,
  ThreeShapeExportAction
} from 'types/ThreeShape';
import { TreatmentDocumentType } from 'types/TreatmentPlanning.types';
import { formatDate, translate } from 'utils';
import useThreeShapeHooks from 'utils/customHooks/threeShape.hooks';

const ThreeShapeFiles: FC<{
  isReupload?: boolean;
  scanFilesReupload?: any;
  getResimFiles?: boolean;
  onResimFilesFromThreeShape?: (
    documents: ThreeShapeDocumentUploadUrl[]
  ) => void;
  /** Overrides `patientDetails.id` for export/polling (e.g. Upload Scans modal). */
  threeShapeTreatmentId?: string;
}> = ({
  isReupload = false,
  scanFilesReupload = [],
  getResimFiles = false,
  onResimFilesFromThreeShape,
  threeShapeTreatmentId
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isTableExpanded, setIsTableExpanded] = useState(false);
  const { clinicId, referralId, patientDetails } = useTreatmentContext();
  const [currentPage, setCurrentPage] = useState(1);

  const scanFiles = isReupload
    ? scanFilesReupload
    : patientDetails?.files?.filter(
        (file) => file.key === TreatmentDocumentType.SCAN
      );

  const {
    handleEndSession,
    threeShapeCases,
    hasNext,
    casesLoading,
    isLoading,
    fetchMoreCases,
    handleExportThreeShapeScans
  } = useThreeShapeHooks({
    clinicId,
    treatmentId: threeShapeTreatmentId ?? patientDetails?.id,
    referralId,
    getResimFiles,
    onResimFilesFromThreeShape: (documents) => {
      if (documents.length > 1) {
        setIsTableExpanded(false);
      }
      onResimFilesFromThreeShape?.(documents);
    },
    ...(isReupload
      ? {
          documentType: TreatmentDocumentType.NEW_SCAN,
          resubmissionStatus: RESUBMISSION_STATUS.RESUBMISSION_PENDING
        }
      : {})
  });

  const handleDownload = (caseData: ThreeShapeCase) => {
    if (!caseData.attachments || caseData.attachments.length === 0) {
      return;
    }

    handleExportThreeShapeScans(
      caseData.id,
      caseData.attachments,
      ThreeShapeExportAction.DOWNLOAD_SCANS
    );
  };

  const handleUploadToScan = (caseData: ThreeShapeCase) => {
    if (!caseData.attachments || caseData.attachments.length === 0) {
      return;
    }

    handleExportThreeShapeScans(
      caseData.id,
      caseData.attachments,
      ThreeShapeExportAction.UPLOAD_SCANS
    );
  };

  const handleLoadMore = () => {
    if (!hasNext || casesLoading) return;
    fetchMoreCases();
  };

  const THREE_SHAPE_TABLE_HEADERS: Column[] = [
    {
      Header: translate('three.shape.patient.name'),
      accessor: 'patientName',
      disableSortBy: true,
      minWidth: 7,
      Cell: (props: CellProps<any, any>) => (
        <div className='line-clamp-3 break-words'>{props.value || '-'}</div>
      )
    },
    {
      Header: translate('three.shape.patient.id'),
      accessor: 'orderNumber',
      disableSortBy: true,
      minWidth: 8,
      Cell: (props: CellProps<any, any>) => (
        <div className='line-clamp-3 break-words'>{props.value || '-'}</div>
      )
    },
    {
      Header: translate('creation.date'),
      accessor: 'createdAt',
      disableSortBy: true,
      minWidth: 6,
      Cell: (props: CellProps<any, any>) => (
        <div className='flex flex-wrap'>
          {props.value
            ? props.value instanceof Date
              ? formatDate(props.value, DATE_FORMAT_LONG_MONTH)
              : formatDate(new Date(props.value), DATE_FORMAT_LONG_MONTH)
            : '-'}
        </div>
      )
    },
    {
      Header: translate('three.shape.comment'),
      accessor: 'description',
      disableSortBy: true,
      minWidth: 18,
      Cell: (props: CellProps<any, any>) => (
        <div
          className='flex flex-wrap line-clamp-3 break-words'
          title={props.value}
        >
          {props.value || '-'}
        </div>
      )
    },
    {
      Header: '',
      accessor: 'action',
      disableSortBy: true,
      minWidth: 14,
      Cell: (props: CellProps<any, any>) => {
        const caseData = props.row.original as ThreeShapeCase;
        return (
          <div className='flex gap-2 items-center justify-end'>
            {caseData.attachments && caseData.attachments.length > 0 && (
              <button
                onClick={() => handleDownload(caseData)}
                className='text-PRIMARY underline hover:text-DARK_BLUE flex items-center gap-1 mr-2 disabled:opacity-50 disabled:cursor-not-allowed'
                disabled={isLoading}
                type='button'
                data-testid='three-shape-download'
              >
                <DownloadIcon className='h-4 w-4 hover:text-PRIMARY hover:cursor-pointer' />
                {translate('actions.download')}
              </button>
            )}
            <button
              onClick={() => handleUploadToScan(caseData)}
              className='disabled:opacity-50 disabled:cursor-not-allowed'
              disabled={isLoading}
              type='button'
              data-testid='three-shape-upload-to-scan'
            >
              <ChipLabel className='bg-LIGHT_BLUE text-DARK_BLUE'>
                {translate('three.shape.upload')}
              </ChipLabel>
            </button>
          </div>
        );
      }
    }
  ];

  const toggleTableVisibility = () => {
    setIsTableExpanded(!isTableExpanded);
  };
  const totalPages = Math.ceil(threeShapeCases.length / 10);
  const renderPageSelection = () => {
    return (
      <div
        className='flex items-center'
        data-testid='three-shape-page-selection'
      >
        {Array.from({ length: totalPages }, (_, i) => {
          return (
            <button
              key={i}
              onClick={() => {
                if (i + 1 !== currentPage) {
                  setCurrentPage(i + 1);
                }
              }}
              type='button'
              className={`px-3 py-1 text-base ${
                currentPage === i + 1 ? ' text-PRIMARY' : ''
              }`}
              data-testid={`three-shape-page-selection-${i + 1}`}
            >
              {i + 1}
            </button>
          );
        })}
      </div>
    );
  };

  const tableData = threeShapeCases.slice(
    (currentPage - 1) * 10,
    currentPage * 10
  );

  useEffect(() => {
    setCurrentPage(Math.ceil(threeShapeCases.length / 10));
  }, [threeShapeCases.length]);

  useEffect(() => {
    if (scanFiles?.length) {
      if (scanFiles?.length < 2) {
        setIsTableExpanded(true);
      } else {
        setIsTableExpanded(false);
      }
    } else setIsTableExpanded(true);
  }, [scanFiles?.length]);

  return (
    <div className='w-full bg-BG_COLOR p-5 rounded-lg'>
      <div className='mb-6 hover:cursor-pointer' role='presentation'>
        <div
          className='flex items-center justify-between mb-2'
          onClick={toggleTableVisibility}
          role='presentation'
          data-testid='three-shape-toggle-table-visibility'
        >
          <h1 className='text-17px text-DEFAULT_TEXT flex items-center'>
            {translate('three.shape.suggestions')}
            <img
              src={threeShapeImage}
              alt='three shape'
              className='ml-3 w-22 mb-1'
            />
          </h1>
          {isTableExpanded ? (
            <CollapseIcon className='w-4 h-4' />
          ) : (
            <ExpandIcon className='w-4 h-4' />
          )}
        </div>
        <div className='flex items-center justify-between'>
          <div className='flex items-center gap-3'>
            <span className='text-17px'>{patientDetails?.threeShapeEmail}</span>
            <If condition={isTableExpanded}>
              <div
                onClick={() => handleEndSession('switch-account')}
                role='presentation'
                className='bg-[#E5E8F3] rounded-full flex items-center justify-center text-sm gap-2 px-2 py-1'
                data-testid='three-shape-switch-account'
              >
                <SwitchReloadIcon className='w-4.5 h-4.5' />
                <span className='text-sm text-[#40528C] mt-1'>
                  {translate('three.shape.switch.account')}
                </span>
              </div>
            </If>
          </div>
          <If condition={isTableExpanded}>
            <button
              className='text-sm flex gap-2'
              onClick={() => handleEndSession('log-out')}
              type='button'
              data-testid='three-shape-logout'
            >
              <LogoutIcon className='w-5 h-5' />
              {translate('actions.logout')}
            </button>
          </If>
        </div>
      </div>

      <div
        className={`transition-all duration-300 ease-in-out overflow-y-hidden overflow-x-auto ${
          isTableExpanded ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div ref={containerRef} className='h-fit'>
          {casesLoading ? (
            <TableSkeleton
              rows={3}
              columns={5}
              columnWidths={['13%', '15%', '12%', '34%', '26%']}
            />
          ) : (
            <>
              <Table
                columns={THREE_SHAPE_TABLE_HEADERS}
                data={tableData}
                noDataMessage={
                  hasNext
                    ? translate(
                        'No cases match this patient name. Please click "more" to load all cases.'
                      )
                    : translate('three.shape.no.cases')
                }
                showRowPointer={false}
                headerClassName='bg-GRAY5 font-semibold border-y border-LINE_GRAY'
                stickyHeader={false}
                tableClassName='bg-BG_COLOR'
                tableDataClassName='border-y border-LINE_GRAY text-sm px-2'
              />

              <div className='flex items-center justify-center mt-4 px-2'>
                <div className='flex items-center'>{renderPageSelection()}</div>
                <button
                  onClick={() => handleLoadMore()}
                  type='button'
                  disabled={!hasNext}
                  className='px-3 py-1 text-base disabled:opacity-50 disabled:cursor-not-allowed underline text-PRIMARY'
                  data-testid='three-shape-load-more'
                >
                  {translate('common.more')}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ThreeShapeFiles;
